/**
 * "About this demo" dialog — ported from the sister app's welcome-dialog,
 * retuned to this repo's two human-in-the-loop workflows (order-event comms
 * and rep-approved share nudges), all on fictive data.
 */

import { ArrowRight, CheckCircle2, CircleDot, Info, Sparkles } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const WELCOME_PARAM = "welcome";

// Module-level latch so the auto-open survives React Strict Mode's dev-only
// mount → unmount → remount dance: the first mount strips `?welcome=1` from
// the URL and sets `pendingWelcome = true`; the remount sees no URL param but
// reads the latch and re-opens the dialog. The latch is cleared when the user
// dismisses the dialog so a manual refresh after that does not re-open it.
let pendingWelcome = false;

export function WelcomeDialog() {
  const [open, setOpen] = useState(false);

  // Auto-open when the URL carries `?welcome=1` — a deep-link hook for popping
  // the overview on a fresh sign-in. Nothing appends it today (the dialog is
  // reached via the top-bar "About this demo" button); wire the login redirect
  // to add it if we want the auto-open-on-login behavior.
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const url = new URL(window.location.href);
    if (url.searchParams.get(WELCOME_PARAM) === "1") {
      url.searchParams.delete(WELCOME_PARAM);
      const cleaned = url.pathname + (url.search ? url.search : "") + url.hash;
      window.history.replaceState(null, "", cleaned);
      pendingWelcome = true;
    }
    // One-shot open driven by the URL/latch — not a render loop, so the
    // generic "no setState in effect" rule does not apply here.
    if (pendingWelcome) {
      setOpen(true);
    }
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      pendingWelcome = false;
    }
    setOpen(next);
  };

  return <WelcomeDialogControlled open={open} onOpenChange={handleOpenChange} />;
}

export function WelcomeDialogTrigger({
  className,
  label = "About this demo",
}: {
  className?: string;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={label}
        className={cn(
          "text-muted hover:bg-accent-light hover:text-foreground inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors",
          className,
        )}
      >
        <Info className="h-5 w-5" />
      </button>
      <WelcomeDialogControlled open={open} onOpenChange={setOpen} />
    </>
  );
}

function WelcomeDialogControlled({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card max-h-[calc(100vh-2rem)] max-w-2xl gap-0 overflow-x-hidden overflow-y-auto p-0 sm:rounded-lg">
        <div
          aria-hidden
          className="bg-primary pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full opacity-[0.06] blur-3xl"
        />
        <div className="relative grid grid-cols-1 gap-6 p-6 sm:p-8">
          <DialogHeader>
            <span className="text-primary text-xs font-medium tracking-[0.18em] uppercase">
              J&amp;J Vision · Proactive Comms
            </span>
            <DialogTitle className="font-heading text-foreground mt-2 text-2xl leading-tight font-normal tracking-tight sm:text-3xl">
              Proactive customer communications, with a human in the loop.
            </DialogTitle>
            <DialogDescription className="text-muted text-sm leading-relaxed">
              The agent watches two things: every order moving through its lifecycle (including
              backorders), and preferred accounts tracking against their order-share benefit tiers.
              It emails customers on order events automatically, and drafts share-tier nudges for a
              rep to review and approve before anything sends. Every send — and every suppression —
              is written to an audit log. Everything shown here runs on fictive data, to demonstrate
              what&rsquo;s possible with current technology.
            </DialogDescription>
          </DialogHeader>

          <div className="border-border bg-background flex flex-col gap-3 rounded-md border p-5">
            <div className="flex items-center gap-2">
              <Sparkles className="text-primary h-4 w-4" />
              <p className="text-foreground text-sm font-semibold">What&rsquo;s live today</p>
            </div>
            <ul className="text-foreground space-y-2 text-sm leading-6">
              <Bullet>Order-lifecycle event emails, sent automatically as orders progress</Bullet>
              <Bullet>A per-order sent-email log you can review at any time</Bullet>
              <Bullet>
                Preferred-account share tiers, with rep-approved nudges drafted for accounts
                trending below target
              </Bullet>
              <Bullet>Full audit logging across every send, suppression, and approval</Bullet>
            </ul>
            <div className="mt-2 flex items-center gap-2">
              <CircleDot className="text-faint h-4 w-4" />
              <p className="text-muted text-sm font-semibold">Next on the roadmap</p>
            </div>
            <ul className="text-muted-foreground space-y-2 text-sm leading-6">
              <Bullet tone="muted">
                Richer customer notification preferences, beyond opt in / opt out
              </Bullet>
              <Bullet tone="muted">Configurable tier thresholds per account segment</Bullet>
              <Bullet tone="muted">
                Moving from fictive data to governed, real data inside the J&amp;J environment
              </Bullet>
            </ul>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild size="lg" onClick={() => onOpenChange(false)}>
              <Link to="/ordering">Open ordering comms</Link>
            </Button>
            <Button asChild size="lg" variant="outline" onClick={() => onOpenChange(false)}>
              <Link to="/share">Open order-share comms</Link>
            </Button>
          </div>

          {/* Quiet route into the full docs — closes the dialog on navigate
              (the shell keeps it mounted, so it won't self-dismiss otherwise). */}
          <Link
            to="/guide"
            onClick={() => onOpenChange(false)}
            className="text-muted hover:text-primary -mt-2 inline-flex items-center gap-1 text-sm font-medium transition-colors"
          >
            Read the full guide
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>

          <p className="text-muted-foreground text-xs">
            Fictive data for the sake of demonstrating functionality.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Bullet({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "muted";
}) {
  // Live items get a green check; roadmap items a muted "not yet" dot.
  const Icon = tone === "muted" ? CircleDot : CheckCircle2;
  return (
    <li className="flex items-start gap-2">
      <Icon
        className={cn(
          "mt-0.5 h-4 w-4 shrink-0",
          tone === "muted" ? "text-faint" : "text-[#328714]",
        )}
      />
      <span>{children}</span>
    </li>
  );
}
